/* eslint-disable @typescript-eslint/no-unused-vars */
import { AttachFile, Close, SaveAlt, SaveAltRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Buffer } from "buffer";
import saveAs from "file-saver";
import React, { useContext, useEffect, useState } from "react";
import Loader from "src/Components/Common/Loader";
import useFetch from "src/Components/Common/useFetch";
import { ViewEmailThreadRes } from "src/Components/Recon360/DisputeResolution/Common/ViewReplyMailThread";
import { MsmeMailThread, UseCaseType } from "src/entity/recon-entity/ReconInterfaces";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import FormatDate from "src/Utils/DateUtils";
import { CommunicationDetails } from "../../BalanceConfirmationPortal/BalanceConfirmationPortalCommon";
import { DEFAULT_BRANCH_CODE, DEFAULT_COMPANY_ID } from "../../CommonLegacy/CommonComponents";
import { NdButton } from "../../MsmePartnerPortal/CommonComponents";
import BcNewContext from "../Components/BcNewContext";

export const MailAttachmentsDialog = ({
  open,
  setOpen,
  viewThreadAction,
  updateRespAction,
  setBalanceConfirmation,
}) => {
  const { companyId, branchCode, currentRow } = useContext(BcNewContext);
  // const { businessPartnerName } = useContext(PartnerCommsContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [mailThreads, setMailThreads] = useState<MsmeMailThread[]>([]);
  // const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailsLists, setEmailsLists] = useState<{
    ownSideEmails: string[];
    toEmails: string[];
    ccEmails: string[];
  }>({
    ownSideEmails: [],
    toEmails: [],
    ccEmails: [],
  });

  const handleClose = () => {
    setOpen(false);
  };

  const ViewEmailThread = () => {
    setLoading(true);
    return useFetch<ViewEmailThreadRes>(API_ENDPOINTS.VIEW_EMAIL_THREAD.url, "GET", {
      failureMessage: API_ENDPOINTS.VIEW_EMAIL_THREAD.failureMessage,
      config: {
        params: {
          companyId: companyId || DEFAULT_COMPANY_ID,
          branchCode: branchCode || DEFAULT_BRANCH_CODE,
          businessPartnerId: currentRow?.businessPartnerId,
          mappingId: currentRow?.mappingId,
        },
      },
      thenCallBack: (res) => {
        if (res.data) {
          setMailThreads(res.data?.emailThread?.length > 0 ? res.data?.emailThread : []);
          // setEmailSubject(res.data.emailSubject || "");
          setEmailsLists({
            ownSideEmails: res.data.ownSideEmails || [],
            toEmails: res.data.toEmails || [],
            ccEmails: res.data.ccEmails || [],
          });
          setLoading(false);
        }
      },
    });
  };

  const GetCommunicationTypeDetailsForTool = () => {
    return useFetch<CommunicationDetails>("/api/PartnerCommunicationPortal/GetCommunicationTypeDetailsForTool", "GET", {
      failureMessage: "Failed to fetch details for uploading page",
      config: {
        params: {
          companyId: companyId || DEFAULT_COMPANY_ID,
          branchCode: branchCode || DEFAULT_BRANCH_CODE,
          mappingId: currentRow?.mappingId,
          communicationId: currentRow?.newMailInitiationCount,
          businessPartnerId: currentRow?.businessPartnerId,
          useCaseType: UseCaseType.balanceConfirmationBeta,
        },
      },
      thenCallBack: (res) => {
        setBalanceConfirmation(!!res.data?.isBalanceConfirmationEnabled);
      },
    });
  };

  useEffect(() => {
    if (open) {
      ViewEmailThread();
      GetCommunicationTypeDetailsForTool();
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            minWidth: 900,
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#f1f1f1",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "16px 24px",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: 20, fontWeight: 500 }}>
            Mail Attachments for {currentRow?.businessPartnerName}
          </Typography>
          <IconButton edge="end" onClick={handleClose} size="small" sx={{ p: "5px" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        {loading ? (
          <Loader />
        ) : (
          <DialogContent sx={{ px: 3, py: "16px !important" }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              All attachments received in the thread separated by sender's email address
            </Typography>
            <Stack spacing={2}>
              {mailThreads.map(
                (thread, index) =>
                  thread.attachments.length > 0 && (
                    <AttachmentGroup
                      key={index}
                      index={index}
                      totalLength={mailThreads.length}
                      thread={thread}
                      ownSideEmails={emailsLists.ownSideEmails}
                    />
                  )
              )}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button variant="text" color="primary" onClick={viewThreadAction}>
            View Thread
          </Button>
          <NdButton variant="contained" color="primary" onClick={updateRespAction} disabled={currentRow?.isApproved}>
            Update Response
          </NdButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AttachmentGroup = ({
  thread,
  index,
  totalLength,
  ownSideEmails,
}: {
  thread: MsmeMailThread;
  index: number;
  totalLength: number;
  ownSideEmails: string[];
}) => {
  const { companyId, branchCode, currentRow } = useContext(BcNewContext);

  const GetAttachmentsZip = async () => {
    return useFetch<{ buffer: string; fileName: string }>(API_ENDPOINTS.GET_ATTACHMENTS_ZIP.url, "GET", {
      failureMessage: API_ENDPOINTS.GET_ATTACHMENTS_ZIP.failureMessage,
      config: {
        params: {
          companyId: companyId,
          branchCode: branchCode,
          businessPartnerId: currentRow?.businessPartnerId,
          mappingId: currentRow?.mappingId,
          mailThreadIndex: totalLength - index - 1, // reverse order
        },
      },
      thenCallBack: (res) => {
        const zipData = Buffer.from(res.data.buffer, "base64");
        const fileType = "application/x-zip-compressed";
        const blob = new Blob([zipData], { type: fileType });
        saveAs(blob, res.data.fileName);
      },
    });
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "#f5f5f5", borderRadius: 1, border: "1px solid rgba(0, 0, 0, 0.12)" }}>
      <Stack direction="row" spacing={2} sx={{ p: "12px 16px" }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            bgcolor: ownSideEmails.includes(thread.from) ? "#27B27C" : "#1E88E5",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AttachFile sx={{ color: "#fff" }} />
        </Box>
        <Stack>
          <Typography variant="body1">{thread.from}</Typography>
          <Typography variant="caption" color="textSecondary">
            {FormatDate(thread.emailTime).toString()}
          </Typography>
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={<SaveAlt />}
          sx={{
            color: "#541c4c",
            textTransform: "uppercase",
            fontSize: 13,
            fontWeight: 500,
          }}
          onClick={GetAttachmentsZip}
        >
          Download
        </Button>
      </Stack>
      <Stack direction="row" flexWrap={"wrap"} gap={1} sx={{ p: 1 }}>
        {thread.attachments.map((attach, index) => (
          <Chip
            key={index}
            deleteIcon={<SaveAltRounded />}
            onDelete={() => {
              // Download Attachment
              const fileName = thread?.attachments[index];
              const fileUri = thread?.s3Link[index];

              const link = document.createElement("a");
              link.href = fileUri;
              link.download = fileName;
              link.style.display = "none";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            onClick={() => {}}
            label={attach}
            sx={{
              "& .MuiChip-label": {
                fontSize: 13,
                fontFamily: "inherit",
              },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};
